import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Subscription } from 'rxjs';
import { ProgressSpinnerOverlayComponent } from '../../../shared/components/progress-spinner-overlay/progress-spinner-overlay.component';
import { NotificationPreference } from '../../models/notification-preference';
import { NotificationType } from '../../models/notification-type';

@Component({
  selector: 'app-notification-preferences-form',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    NgxMaskDirective,
    ReactiveFormsModule,
    ProgressSpinnerOverlayComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './notification-preferences-form.component.html',
  styleUrl: './notification-preferences-form.component.scss',
})
export class NotificationPreferencesFormComponent implements OnDestroy {
  notificationTypes: NotificationType[] | null = null;
  notificationForm!: FormGroup;
  private _notificationPreferences: NotificationPreference | null = null;
  private initialFormValue: unknown;
  private subscribe?: Subscription;

  @Input() set notificationType(value: NotificationType[] | null) {
    if (!value) return;
    this.notificationTypes = value;
    this.syncForm();
  }

  @Input() set notificationPreferences(value: NotificationPreference | null) {
    if (!value) return;
    this._notificationPreferences = value;
    this.syncForm();
  }

  @Input() showSaveButton: boolean = true;

  // Driven by the store rather than latched locally on submit: a rejected save must leave the form
  // editable, and a flag only ever set to true blocks it behind the overlay forever.
  //
  // Also locks the form while saving. The save overlay is dismissible, and a successful save re-feeds
  // `notificationPreferences`, which rebuilds the form: anything typed after dismissal would otherwise
  // be silently replaced by the server's copy.
  @Input() set isSaving(value: boolean) {
    this._isSaving = value;
    this.applySavingLock();
  }
  get isSaving(): boolean {
    return this._isSaving;
  }
  private _isSaving = false;

  /** Form-level message for a save the API rejected, e.g. the no-clear rule naming a field. */
  @Input() errorMessage: string | null = null;
  @Output() saveNotificationPreferencesEvent = new EventEmitter<NotificationPreference>();
  @Output() cancelNotificationPreferencesEvent = new EventEmitter<void>();

  get phoneControl(): FormControl {
    return this.notificationForm.get('phone') as FormControl;
  }

  private syncForm(): void {
    if (!this.notificationTypes || !this._notificationPreferences) return;

    const preferences = this._notificationPreferences;
    const notificationTypeControls: { [key: string]: FormControl<boolean> } = {};
    this.notificationTypes.forEach(type => {
      const isChecked = preferences.notificationTypes.some(
        selected => selected.notificationTypeId === type.notificationTypeId
      );
      notificationTypeControls[type.name] = new FormControl<boolean>(isChecked, { nonNullable: true });
    });

    // Both contact values may be null: a proxy inherits the patient's consent while the contact
    // backfills only reach Self links, so live consent with a blank field is a routine arrival
    // state. Rendered empty and left to the conditional validators, which is what blocks Save.
    this.notificationForm = new FormGroup({
      email: new FormControl<string>(this._notificationPreferences.emailAddress ?? '', [Validators.email]),
      phone: new FormControl<string>(this._notificationPreferences.mobilePhoneNumber ?? '', [
        Validators.pattern(/^\d{10}$/),
      ]),
      notificationTypes: new FormGroup(notificationTypeControls),
    });

    this.initialFormValue = this.notificationForm.getRawValue();

    const notificationTypesControl = this.notificationForm.get('notificationTypes');
    if (!notificationTypesControl) return;

    //syncForm re-runs whenever either input changes, so drop the previous subscription first
    this.subscribe?.unsubscribe();
    this.subscribe = notificationTypesControl.valueChanges.subscribe((types: Record<string, boolean>) => {
      this.updateValidators(types);
    });

    this.updateValidators(notificationTypesControl.value);

    // The rebuilt group starts enabled; carry over the lock if a save is still in flight.
    this.applySavingLock();
  }

  private applySavingLock(): void {
    if (!this.notificationForm) return;

    if (this._isSaving) {
      this.notificationForm.disable({ emitEvent: false });
    } else if (this.notificationForm.disabled) {
      this.notificationForm.enable({ emitEvent: false });
    }
  }

  private updateValidators(preferences: Record<string, boolean>): void {
    const emailControl = this.notificationForm.get('email');
    const phoneControl = this.notificationForm.get('phone');

    if (!emailControl || !phoneControl) return;

    if (preferences['Email'] === true) {
      emailControl.setValidators([Validators.required, Validators.email]);
    } else {
      emailControl.clearValidators();
    }

    if (preferences['Phone'] === true) {
      phoneControl.setValidators([Validators.required, Validators.pattern(/^\d{10}$/)]);
    } else {
      phoneControl.clearValidators();
    }

    emailControl.updateValueAndValidity();
    phoneControl.updateValueAndValidity();
  }

  getSelectedNotificationTypeIds(): number[] {
    if (!this.notificationForm || !this.notificationForm.get('notificationTypes')) return [];

    const notificationTypes = this.notificationTypes;
    if (!notificationTypes) return [];

    const selectedIds: number[] = [];
    const formValues = this.notificationForm.get('notificationTypes')?.value || {};

    notificationTypes.forEach(type => {
      if (formValues[type.name]) {
        selectedIds.push(type.notificationTypeId);
      }
    });

    return selectedIds;
  }

  updateNotificationPreferences(): void {
    // Every Save for this form funnels through here (its own, the settings page's, the schedule dialog's).
    if (this.isSaving) {
      return;
    }

    if (this.notificationForm.invalid) {
      // Material renders mat-error only when the control is touched or the form was submitted, and
      // neither happens here: the blank-contact state arrives pristine, and both Save buttons call
      // this method rather than submitting the form. Without this the user clicks Save and sees
      // nothing at all.
      this.notificationForm.markAllAsTouched();
      return;
    }

    if (JSON.stringify(this.notificationForm.getRawValue()) !== JSON.stringify(this.initialFormValue)) {
      const notificationTypes = this.notificationTypes;
      if (!notificationTypes) return;

      const updatedPreferences: NotificationPreference = {
        // back out as null, not '': the API stores what it is sent, and the 56948 pre-exposure
        // checks look for NULL, so a blank saved as an empty string would hide that user from them
        emailAddress: this.notificationForm.value.email || null,
        mobilePhoneNumber: this.notificationForm.value.phone || null,
        notificationTypes: notificationTypes.filter(type =>
          this.getSelectedNotificationTypeIds().includes(type.notificationTypeId)
        ),
      };

      this.saveNotificationPreferencesEvent.emit(updatedPreferences);
    } else {
      this.cancelNotificationPreferencesEvent.emit();
    }
  }

  ngOnDestroy(): void {
    this.subscribe?.unsubscribe();
  }
}
